import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context';
import { api } from '@/services/api';
import styles from './ForcePasswordChangePage.module.css';

export function ForcePasswordChangePage() {
  const navigate = useNavigate();
  const { clearMustChangePassword, logout, isAuthenticated, mustChangePassword, isPlatformAdmin } = useAuth();

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // Redirect if user doesn't need to change password or isn't authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    if (isPlatformAdmin) {
      navigate('/platform/force-password-change', { replace: true });
      return;
    }
    if (!mustChangePassword) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, mustChangePassword, isPlatformAdmin, navigate]);

  // Don't render if user shouldn't be on this page
  if (!isAuthenticated || isPlatformAdmin || !mustChangePassword) {
    return null;
  }

  const validateForm = (): string | null => {
    if (!formData.currentPassword) {
      return 'Current password is required';
    }
    if (!formData.newPassword) {
      return 'New password is required';
    }
    if (formData.newPassword.length < 8) {
      return 'New password must be at least 8 characters';
    }
    if (formData.newPassword !== formData.confirmPassword) {
      return 'Passwords do not match';
    }
    if (formData.currentPassword === formData.newPassword) {
      return 'New password must be different from current password';
    }
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.put('/api/v1/auth/password', {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to change password');
      }

      // Clear the mustChangePassword flag
      clearMustChangePassword();

      // Redirect to dashboard
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const getPasswordStrength = (password: string): { label: string; color: string; width: string } => {
    if (!password) return { label: '', color: '', width: '0%' };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return { label: 'Weak', color: '#EF4444', width: '33%' };
    if (strength <= 3) return { label: 'Fair', color: '#F59E0B', width: '66%' };
    return { label: 'Strong', color: '#10B981', width: '100%' };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  return (
    <div className={styles.page}>
      {/* Animated Background Particles */}
      <div className={styles.particlesContainer}>
        {[...Array(15)].map((_, i) => (
          <div key={i} className={styles.particle} style={{
            '--delay': `${i * 0.5}s`,
            '--x': `${Math.random() * 100}%`,
            '--duration': `${15 + Math.random() * 20}s`,
          } as React.CSSProperties} />
        ))}
      </div>

      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.iconWrapper}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h1 className={styles.title}>Password Change Required</h1>
          <p className={styles.subtitle}>
            For security purposes, you must change your password before continuing.
          </p>
        </div>

        {/* Security Notice */}
        <div className={styles.notice}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="M9 12l2 2 4-4" />
          </svg>
          <span>Your new password will be encrypted with AES-256</span>
        </div>

        {/* Form */}
        <form className={styles.form} onSubmit={handleSubmit}>
          {error && (
            <div className={styles.error}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <div className={`${styles.inputGroup} ${focusedField === 'current' ? styles.inputGroupFocused : ''}`}>
            <label className={styles.label}>
              <span className={styles.labelText}>Current Password</span>
            </label>
            <div className={styles.inputWrapper}>
              <div className={styles.inputIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <input
                type="password"
                placeholder="Enter your current password"
                value={formData.currentPassword}
                onChange={e => setFormData({ ...formData, currentPassword: e.target.value })}
                onFocus={() => setFocusedField('current')}
                onBlur={() => setFocusedField(null)}
                required
                className={styles.input}
                autoComplete="current-password"
              />
            </div>
          </div>

          <div className={`${styles.inputGroup} ${focusedField === 'new' ? styles.inputGroupFocused : ''}`}>
            <label className={styles.label}>
              <span className={styles.labelText}>New Password</span>
              {passwordStrength.label && (
                <span className={styles.strengthLabel} style={{ color: passwordStrength.color }}>
                  {passwordStrength.label}
                </span>
              )}
            </label>
            <div className={styles.inputWrapper}>
              <div className={styles.inputIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                </svg>
              </div>
              <input
                type="password"
                placeholder="Enter new password (min. 8 characters)"
                value={formData.newPassword}
                onChange={e => setFormData({ ...formData, newPassword: e.target.value })}
                onFocus={() => setFocusedField('new')}
                onBlur={() => setFocusedField(null)}
                required
                className={styles.input}
                autoComplete="new-password"
              />
            </div>
            {formData.newPassword && (
              <div className={styles.strengthBar}>
                <div
                  className={styles.strengthFill}
                  style={{ width: passwordStrength.width, backgroundColor: passwordStrength.color }}
                />
              </div>
            )}
          </div>

          <div className={`${styles.inputGroup} ${focusedField === 'confirm' ? styles.inputGroupFocused : ''}`}>
            <label className={styles.label}>
              <span className={styles.labelText}>Confirm New Password</span>
              {formData.confirmPassword && formData.newPassword === formData.confirmPassword && (
                <span className={styles.matchLabel}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  Match
                </span>
              )}
            </label>
            <div className={styles.inputWrapper}>
              <div className={styles.inputIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4" />
                  <circle cx="12" cy="12" r="10" />
                </svg>
              </div>
              <input
                type="password"
                placeholder="Confirm your new password"
                value={formData.confirmPassword}
                onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })}
                onFocus={() => setFocusedField('confirm')}
                onBlur={() => setFocusedField(null)}
                required
                className={styles.input}
                autoComplete="new-password"
              />
            </div>
          </div>

          <button type="submit" disabled={isLoading} className={styles.submitButton}>
            {isLoading ? (
              <>
                <span className={styles.spinner} />
                <span>Updating Password...</span>
              </>
            ) : (
              <>
                <span>Update Password & Continue</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </>
            )}
            <div className={styles.buttonGlow} />
          </button>
        </form>

        {/* Logout Option */}
        <div className={styles.footer}>
          <button type="button" onClick={handleLogout} className={styles.logoutLink}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Sign out and login with different account
          </button>
        </div>
      </div>
    </div>
  );
}
